import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Pagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { getAdminBlogs, deleteBlog, getBlogPreview } from "../api/BlogApi";
import { getApiErrorMessage } from "../utils/apiError";

const LIMIT = 10;

// decodes text/escaped button tags (including forms and embedded scripts) to real HTML
const decodeButtonHtml = (html) => {
  if (!html) return "";

  let result = html;

  // 1. Convert escaped <a>...</a> wrapping buttons or in content
  result = result.replace(/(&lt;a[\s\S]*?&gt;)([\s\S]*?)(&lt;\/\s*a\s*&gt;)/gi, (match, openTag, content) => {
    const cleanOpen = openTag.replace(/<[^>]+>/g, "");
    const decodedOpen = cleanOpen
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, "&")
      .trim();
    return `${decodedOpen}${content}</a>`;
  });

  // 2. Convert escaped or colored <form>...<script>...</script>...</form>
  result = result.replace(/(&lt;form[\s\S]*?&gt;)([\s\S]*?)(&lt;\/\s*form\s*&gt;)/gi, (match, openTag, content) => {
    const cleanOpen = openTag.replace(/<[^>]+>/g, "").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, "&").trim();
    return `${cleanOpen}${content}</form>`;
  });
  result = result.replace(/(&lt;script[\s\S]*?&gt;)([\s\S]*?)(&lt;\/\s*script\s*&gt;)/gi, (match, openTag, content) => {
    const cleanOpen = openTag.replace(/<[^>]+>/g, "").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, "&").trim();
    return `${cleanOpen}${content}</script>`;
  });

  // 3. Match any button pattern, whether escaped (&lt;button...) or raw (<button...)
  result = result.replace(/(&lt;button[\s\S]*?&gt;)([\s\S]*?)(&lt;\/\s*button\s*&gt;)/gi, (match, openTag, content) => {
    const cleanOpen = openTag.replace(/<[^>]+>/g, "");
    let decodedOpen = cleanOpen
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, "&")
      .trim();

    if (!/type\s*=/i.test(decodedOpen)) {
      decodedOpen = decodedOpen.replace(/^<button/i, '<button type="button"');
    }

    const cleanContent = content.replace(/^<\/span>/, "").replace(/<span[^>]*>$/, "");
    return `${decodedOpen}${cleanContent}</button>`;
  });

  // 4. Convert any remaining un-paired &lt;button...&gt;, &lt;form...&gt;, &lt;script...&gt;
  result = result.replace(/&lt;button[\s\S]*?&gt;/gi, (fullMatch) => {
    const clean = fullMatch.replace(/<[^>]+>/g, "");
    let decoded = clean
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, "&")
      .trim();
    if (!/type\s*=/i.test(decoded)) {
      decoded = decoded.replace(/^<button/i, '<button type="button"');
    }
    return decoded;
  });
  result = result.replace(/&lt;form[\s\S]*?&gt;/gi, (fullMatch) => {
    return fullMatch.replace(/<[^>]+>/g, "").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, "&").trim();
  });
  result = result.replace(/&lt;script[\s\S]*?&gt;/gi, (fullMatch) => {
    return fullMatch.replace(/<[^>]+>/g, "").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, "&").trim();
  });
  result = result.replace(/&lt;\/\s*button\s*&gt;/gi, "</button>");
  result = result.replace(/&lt;\/\s*form\s*&gt;/gi, "</form>");
  result = result.replace(/&lt;\/\s*script\s*&gt;/gi, "</script>");
  result = result.replace(/&lt;\/\s*a\s*&gt;/gi, "</a>");

  // 5. Clean up any orphan spans that wrapped the button tags
  result = result.replace(/<span[^>]*>\s*(<button[^>]*>[\s\S]*?<\/button>)\s*<\/span>/gi, "$1");
  result = result.replace(/<span[^>]*>\s*(<form[^>]*>[\s\S]*?<\/form>)\s*<\/span>/gi, "$1");
  result = result.replace(/<span[^>]*>\s*(<button[^>]*>)/gi, "$1");
  result = result.replace(/(<\/button>)\s*<\/span>/gi, "$1");
  result = result.replace(/<span[^>]*>\s*(<form[^>]*>)/gi, "$1");
  result = result.replace(/(<\/form>)\s*<\/span>/gi, "$1");

  // 6. Ensure any bare Razorpay script (not inside <form>) is wrapped in <form>...</form>
  result = result.replace(/(?:<form[^>]*>[\s\S]*?<\/form>)|(<script[^>]*payment-button\.js[^>]*>[\s\S]*?<\/script>)/gi, (m, bareScript) => {
    if (bareScript) {
      return `<form>${bareScript}</form>`;
    }
    return m;
  });

  return result;
};

// executes embedded <script> tags (like Razorpay payment-button.js) within a DOM container
const runEmbeddedScripts = (container) => {
  if (!container) return;

  // 1. Ensure all Razorpay scripts are inside a <form> before executing to prevent Razorpay alerts
  const rzpScripts = container.querySelectorAll("script[data-payment_button_id], script[src*='payment-button.js']");
  rzpScripts.forEach((script) => {
    if (!script.closest("form") || script.parentElement?.tagName !== "FORM") {
      const form = document.createElement("form");
      script.parentNode.insertBefore(form, script);
      form.appendChild(script);
    }
  });

  // 2. Execute Razorpay scripts inside forms
  const forms = container.querySelectorAll("form");
  forms.forEach((form) => {
    const script = form.querySelector("script");
    if (!script) return;
    // If a payment button was already injected by Razorpay in this form, do not re-run
    if (form.querySelector(".razorpay-payment-button")) {
      return;
    }
    if (script.getAttribute("data-executed") === "true") {
      return;
    }
    const newScript = document.createElement("script");
    Array.from(script.attributes).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value);
    });
    newScript.setAttribute("data-executed", "true");
    newScript.async = true;
    if (script.innerHTML) {
      newScript.appendChild(document.createTextNode(script.innerHTML));
    }
    if (script.parentNode) {
      script.parentNode.replaceChild(newScript, script);
    }
  });

  // 3. Execute any other non-Razorpay standalone scripts
  const standaloneScripts = container.querySelectorAll("script:not(form script)");
  standaloneScripts.forEach((oldScript) => {
    if (oldScript.getAttribute("data-executed") === "true") return;
    if (oldScript.src && oldScript.src.includes("payment-button.js")) return;
    if (oldScript.hasAttribute("data-payment_button_id")) return;

    const newScript = document.createElement("script");
    Array.from(oldScript.attributes).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value);
    });
    newScript.setAttribute("data-executed", "true");
    newScript.async = true;
    if (oldScript.innerHTML) {
      newScript.appendChild(document.createTextNode(oldScript.innerHTML));
    }
    if (oldScript.parentNode) {
      oldScript.parentNode.replaceChild(newScript, oldScript);
    }
  });
};

const ManageBlogs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [blogs, setBlogs] = useState([]);
  const [page, setPage] = useState(() => {
    const params = new URLSearchParams(location.search);
    const pageFromQuery = Number(params.get("page"));
    return Number.isInteger(pageFromQuery) && pageFromQuery > 0 ? pageFromQuery : 1;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);

  // preview dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewBlog, setPreviewBlog] = useState(null);
  const previewContentRef = useRef(null);

  useEffect(() => {
    if (previewOpen && !previewLoading && previewContentRef.current) {
      const timer = setTimeout(() => {
        runEmbeddedScripts(previewContentRef.current);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [previewOpen, previewLoading, previewBlog]);

  const fetchBlogs = useCallback(async (pageNo) => {
    try {
      setLoading(true);
      const res = await getAdminBlogs(pageNo, LIMIT);

      // be tolerant of different response shapes
      const data = res?.data ?? res;
      const list = data?.blogs ?? data?.items ?? data?.results ?? (Array.isArray(data) ? data : []);

      // total number of blogs across all pages (try common field names)
      const totalCount =
        data?.total ??
        data?.totalCount ??
        data?.count ??
        data?.pagination?.total ??
        data?.pagination?.totalCount;

      const pages =
        data?.totalPages ??
        data?.pagination?.totalPages ??
        (totalCount != null ? Math.ceil(totalCount / LIMIT) : 1);

      setBlogs(Array.isArray(list) ? list : []);
      // pagination shows only when there is more than one page (i.e. > 10 blogs)
      setTotalPages(pages || 1);
    } catch (err) {
      console.error("Failed to fetch blogs:", err);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageFromQuery = Number(params.get("page"));
    const nextPage = Number.isInteger(pageFromQuery) && pageFromQuery > 0 ? pageFromQuery : 1;

    if (nextPage !== page) {
      setPage(nextPage);
    } else {
      fetchBlogs(page);
    }
  }, [page, location.search, fetchBlogs]);

  const handlePageChange = (_, value) => {
    setPage(value);
    navigate(`/manage-blogs?page=${value}`, { replace: true });
  };

  const handleCreateBlog = () => {
    navigate("/create-blog", { state: { returnPage: page } });
  };

  const handleEditBlog = (blog) => {
    const id = blog.id ?? blog._id;
    if (!id) return;
    navigate(`/edit-blog/${id}?returnPage=${page}`, { state: { returnPage: page } });
  };

  const handlePreview = async (blog) => {
    const id = blog.id ?? blog._id;
    if (!id) return;
    setPreviewOpen(true);
    setPreviewBlog(null);
    try {
      setPreviewLoading(true);
      const res = await getBlogPreview(id);
      setPreviewBlog(res?.data ?? res);
    } catch (err) {
      console.error("Failed to load blog preview:", err);
      alert(getApiErrorMessage(err, "Failed to load preview ❌"));
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDeleteClick = (blog) => {
    const id = blog.id ?? blog._id;
    if (!id) return;
    setBlogToDelete(blog);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!blogToDelete) return;

    const id = blogToDelete.id ?? blogToDelete._id;
    if (!id) return;

    try {
      setDeletingId(id);
      const res = await deleteBlog(id);

      if (res?.success === false) {
        throw new Error(res?.message || "Failed to delete blog ❌");
      }

      toast.success("Blog deleted successfully!");
      setDeleteDialogOpen(false);
      setBlogToDelete(null);

      // if the last item on a page was deleted, step back a page
      if (blogs.length === 1 && page > 1) {
        setPage((p) => p - 1);
      } else {
        await fetchBlogs(page);
      }
    } catch (err) {
      console.error("Failed to delete blog:", err);
      toast.error(getApiErrorMessage(err, "Failed to delete blog ❌"));
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  const renderTruncatedCell = (value, maxWidth = 240) => {
    const text = value || "-";
    const isFallback = text === "-";

    return (
      <Tooltip title={isFallback ? "" : text} arrow placement="top">
        <Box
          component="span"
          sx={{
            display: "block",
            maxWidth,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {text}
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box p={3} bgcolor="#f5f5f5" minHeight="100vh">
      {/* Top Bar */}
      <Box
        display="grid"
        gridTemplateColumns="1fr auto 1fr"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Button
            variant="contained"
            onClick={() => navigate("/dashboard")}
            sx={{
              background: "linear-gradient(90deg, #7b1fa2 0%, #9c27b0 100%)",
              textTransform: "none",
              px: 3,
            }}
          >
            Back to Dashboard
          </Button>
        </Box>

        <Typography variant="h5" fontWeight={600} textAlign="center">
          Manage Blogs
        </Typography>

        <Box textAlign="right">
          <Button
            variant="contained"
            onClick={handleCreateBlog}
            sx={{ textTransform: "none", backgroundColor: "#1e88e5" }}
          >
            Create Blog
          </Button>
        </Box>
      </Box>

      <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
            <CircularProgress />
          </Box>
        ) : !blogs.length ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="30vh">
            <Typography align="center" variant="h6" color="text.secondary">
              No blogs available
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 600 } }}>
                    <TableCell>#</TableCell>
                    <TableCell>Cover</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Slug</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {blogs.map((blog, index) => (
                    <TableRow key={blog.id ?? blog._id ?? index} hover>
                      <TableCell>{(page - 1) * LIMIT + index + 1}</TableCell>
                      <TableCell>
                        {blog.coverImageSignedUrl || blog.coverImageUrl ? (
                          <img
                            src={blog.coverImageSignedUrl || blog.coverImageUrl}
                            alt={blog.title || "cover"}
                            style={{
                              width: 56,
                              height: 40,
                              objectFit: "cover",
                              borderRadius: 4,
                              border: "1px solid #e5e7eb",
                            }}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        {renderTruncatedCell(blog.title, 280)}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        {renderTruncatedCell(blog.slug, 220)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={blog.status || "-"}
                          size="small"
                          color={blog.status === "published" ? "success" : "default"}
                          sx={{ textTransform: "capitalize" }}
                        />
                      </TableCell>
                      <TableCell>{formatDate(blog.createdAt || blog.created_at)}</TableCell>
                      <TableCell align="center">
                        <Box display="flex" gap={1} justifyContent="center">
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<VisibilityIcon fontSize="small" />}
                            onClick={() => handlePreview(blog)}
                            sx={{ textTransform: "none" }}
                          >
                            Preview
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon fontSize="small" />}
                            onClick={() => handleEditBlog(blog)}
                            sx={{ textTransform: "none" }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon fontSize="small" />}
                            disabled={deletingId === (blog.id ?? blog._id)}
                            onClick={() => handleDeleteClick(blog)}
                            sx={{ textTransform: "none" }}
                          >
                            {deletingId === (blog.id ?? blog._id) ? "Deleting..." : "Delete"}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Blog</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this blog?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            No
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview dialog — shows the blog the way a user would see it */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight={600}>
            Blog Preview
          </Typography>
          <IconButton onClick={() => setPreviewOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: "#fff" }}>
          {previewLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="30vh">
              <CircularProgress />
            </Box>
          ) : previewBlog ? (
            <Box sx={{ maxWidth: 760, mx: "auto", py: 2 }}>
              {(previewBlog.coverImageSignedUrl || previewBlog.coverImageUrl) && (
                <Box
                  component="img"
                  src={previewBlog.coverImageSignedUrl || previewBlog.coverImageUrl}
                  alt={previewBlog.title || "cover"}
                  sx={{
                    width: "100%",
                    maxHeight: 320,
                    objectFit: "cover",
                    borderRadius: 2,
                    mb: 3,
                  }}
                />
              )}

              <Typography variant="h4" fontWeight={700} gutterBottom>
                {previewBlog.title}
              </Typography>

              <Box display="flex" gap={1} alignItems="center" mb={3}>
                <Chip
                  label={previewBlog.status || "-"}
                  size="small"
                  color={previewBlog.status === "published" ? "success" : "default"}
                  sx={{ textTransform: "capitalize" }}
                />
                {previewBlog.createdAt && (
                  <Typography variant="caption" color="text.secondary">
                    {new Date(previewBlog.createdAt).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })}
                  </Typography>
                )}
              </Box>

              {/* render the stored HTML content exactly as a user would see it */}
              <Box
                ref={previewContentRef}
                sx={{
                  lineHeight: 1.7,
                  fontSize: "16px",
                  color: "#1f2937",
                  "& img": { maxWidth: "100%", borderRadius: 1, my: 1 },
                  "& h1": { fontSize: "1.6rem", fontWeight: 700, mt: 2 },
                  "& h2": { fontSize: "1.35rem", fontWeight: 700, mt: 2 },
                  "& p": { my: 1 },
                  "& ul, & ol": { pl: 3 },
                  // admin preview keeps visible grid lines so the author can see
                  // the table structure; the web app renders these borderless
                  "& table": {
                    width: "100%",
                    borderCollapse: "collapse",
                    my: 1.5,
                  },
                  "& td, & th": {
                    padding: "8px",
                    verticalAlign: "top",
                    border: "1px solid #ccc",
                  },
                  "& button": {
                    cursor: "pointer",
                    padding: "8px 18px",
                    borderRadius: "6px",
                    border: "1px solid #9c27b0",
                    backgroundColor: "#9c27b0",
                    color: "#ffffff",
                    fontSize: "14px",
                    fontWeight: 600,
                    display: "inline-block",
                    my: 0.5,
                    fontFamily: "inherit",
                    "&:hover": {
                      opacity: 0.9,
                    },
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: decodeButtonHtml(
                    previewBlog.contentSigned ||
                    previewBlog.contentHtml ||
                    previewBlog.content ||
                    ""
                  ),
                }}
              />
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ManageBlogs;
